import { useCallback, useEffect, useRef, useState } from 'react';
import type { McFinishAudit, McSubmissionResult } from '../services/api';
import { decryptMcBootstrapCipher } from '../utils/mcBootstrapDecrypt';
import {
  loadMcAssemblyExports,
  type McWasmExports,
} from '../utils/mcWasmExports';

export interface McPlayStateDto {
  playSessionId: string;
  quizId: string;
  currentQuestionIndex: number;
  totalScore: number;
  pointsPerQuestion: number[];
  isSessionComplete: boolean;
}

const WASM_BASE =
  (process.env.VITE_MC_WASM_BASE ?? '/wasm/mc').replace(/\/$/, '');

let dotnetModulePromise: Promise<{
  getAssemblyExports: (name: string) => Promise<Record<string, unknown>>;
  getConfig: () => { mainAssemblyName: string };
}> | null = null;

async function loadWasmExports(): Promise<McWasmExports> {
  if (!dotnetModulePromise) {
    dotnetModulePromise = (async () => {
      const dotnetUrl = `${WASM_BASE}/_framework/dotnet.js`;
      const bootConfigUrl = `${WASM_BASE}/_framework/blazor.boot.json`;
      const mod = await import(/* webpackIgnore: true */ dotnetUrl);
      const dotnet = mod.dotnet as
        | {
            withConfigSrc: (src: string) => { create: () => Promise<unknown> };
            create: () => Promise<unknown>;
          }
        | undefined;
      if (!dotnet?.create) {
        throw new Error('MC WASM: dotnet runtime not found.');
      }
      const runtime = dotnet.withConfigSrc
        ? await dotnet.withConfigSrc(bootConfigUrl).create()
        : await dotnet.create();
      return runtime as {
        getAssemblyExports: (name: string) => Promise<Record<string, unknown>>;
        getConfig: () => { mainAssemblyName: string };
      };
    })();
  }

  const runtime = await dotnetModulePromise;
  const config = runtime.getConfig();
  return loadMcAssemblyExports(runtime.getAssemblyExports, config.mainAssemblyName);
}

export function useMcWasm(engineBootstrapCipher: string | undefined) {
  const exportsRef = useRef<McWasmExports | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!engineBootstrapCipher?.trim()) {
      setReady(false);
      setError(null);
      return;
    }

    setReady(false);
    setError(null);

    (async () => {
      try {
        const wasm = await loadWasmExports();
        if (cancelled) return;
        const keyB64 = wasm.getBootstrapKeyBase64();
        const plainB64 = await decryptMcBootstrapCipher(engineBootstrapCipher, keyB64);
        if (cancelled) return;
        const initResult = wasm.initializeSessionFromPlaintext(plainB64);
        const initError =
          typeof initResult === 'string' ? initResult.trim() : 'Failed to initialize MC engine.';
        if (initError) {
          setError(initError);
          setReady(false);
          return;
        }
        exportsRef.current = wasm;
        setError(null);
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'MC WASM load failed.');
        setReady(false);
      }
    })();

    return () => {
      cancelled = true;
      exportsRef.current?.disposeSession();
      exportsRef.current = null;
      setReady(false);
    };
  }, [engineBootstrapCipher]);

  const submitAnswer = useCallback(
    async (questionId: string, answerId: string): Promise<McSubmissionResult | null> => {
      const wasm = exportsRef.current;
      if (!wasm) return null;
      const json = wasm.submitAnswer(questionId, answerId);
      return JSON.parse(json) as McSubmissionResult;
    },
    []
  );

  const getPlayState = useCallback(async (): Promise<McPlayStateDto | null> => {
    const wasm = exportsRef.current;
    if (!wasm) return null;
    const json = wasm.getPlayState();
    return JSON.parse(json) as McPlayStateDto;
  }, []);

  const buildFinishPayload = useCallback((): McFinishAudit | null => {
    const wasm = exportsRef.current;
    if (!wasm) return null;
    const json = wasm.buildFinishPayload();
    if (!json || json === '{}') return null;
    return JSON.parse(json) as McFinishAudit;
  }, []);

  const disposeSession = useCallback(() => {
    exportsRef.current?.disposeSession();
    exportsRef.current = null;
    setReady(false);
  }, []);

  return {
    ready,
    error,
    submitAnswer,
    getPlayState,
    buildFinishPayload,
    disposeSession,
  };
}
